import { useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowRight, Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { storeAuthToken } from '@/lib/session'

import { useCurrentUserQuery } from '../api/useCurrentUser'
import { useLogin } from '../api/useLogin'
import { useAuth } from './auth-context'

const schema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  rememberMe: z.boolean().optional(),
})

type FormData = z.infer<typeof schema>

export function LoginForm() {
  const { login } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [cardActivated, setCardActivated] = useState(false)
  const [formOpened, setFormOpened] = useState(false)
  const [formReady, setFormReady] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const { redirect } = useSearch({ from: '/login' })
  const loginMutation = useLogin()
  const currentUserQuery = useCurrentUserQuery({ enabled: false })

  const form = useForm<FormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
    mode: 'onChange',
  })

  useEffect(() => {
    setCardActivated(true)
    setFormOpened(true)
  }, [])

  useEffect(() => {
    if (!formOpened) {
      setFormReady(false)
      return
    }
    const frame = requestAnimationFrame(() => setFormReady(true))
    return () => cancelAnimationFrame(frame)
  }, [formOpened])

  async function onSubmit(values: FormData) {
    try {
      setIsLoading(true)
      const validatedData = schema.parse(values)

      const response = await loginMutation.mutateAsync({
        usernameOrEmail: validatedData.email,
        password: validatedData.password,
        rememberMe: validatedData.rememberMe,
      })

      const maybeToken = response.data as any
      const accessToken: string | undefined = maybeToken?.accessToken
      const tokenType: string | undefined = maybeToken?.tokenType || 'Bearer'

      if (!accessToken) {
        const message = (maybeToken?.data?.message || maybeToken?.message || 'Invalid credentials') as string
        throw new Error(message)
      }

      storeAuthToken(accessToken, tokenType, validatedData.rememberMe)
      login({ email: validatedData.email, roles: ['ROLE_USER'] })
      toast.success('Signed in successfully')

      const safeTo = (() => {
        const fallback = '/leave/calendar'
        if (!redirect)
          return fallback
        try {
          const u = new URL(redirect, window.location.origin)
          const pathname = u.pathname
          const search = u.search || ''

          // Strip the TanStack Router layout prefix if present
          const cleanPathname = pathname.startsWith('/_authenticated')
            ? pathname.replace('/_authenticated', '')
            : pathname

          // Allowed base paths (pathname-only, search params preserved separately)
          const allowed = new Set([
            '/',
            '/login',
            '/signup',
            '/forgot-password',
            '/reset-password',
            '/leave/applications',
            '/leave/approvals',
            '/leave/calendar',
            '/leave/config/leave-types',
            '/leave/config/holidays',
            '/leave/config/entitlements',
            '/leave/config/departments',
            '/access-denied',
          ])

          if (cleanPathname === '/dashboard')
            return fallback

          if (allowed.has(cleanPathname)) {
            return (cleanPathname + search) as any
          }

          return fallback
        }
        catch {
          if (redirect.startsWith('/')) {
            // Strip /_authenticated prefix for raw string redirects
            const cleaned = redirect.startsWith('/_authenticated')
              ? redirect.replace('/_authenticated', '')
              : redirect
            const [pathname] = cleaned.split('?')
            if (pathname === '/dashboard')
              return fallback
            return cleaned as any
          }
          return fallback
        }
      })()
      const meResult = await currentUserQuery.refetch()
      if (meResult.data) {
        login(meResult.data)
      }
      navigate({ to: safeTo as any, reloadDocument: true })
    }
    catch (error) {
      if (error instanceof z.ZodError) {
        error.issues.forEach((issue) => {
          if (issue.path[0]) {
            form.setError(issue.path[0] as keyof FormData, { message: issue.message })
          }
        })
      }
      else if (error instanceof Error) {
        toast.error(error.message)
      }
    }
    finally {
      setIsLoading(false)
    }
  }

  return (
    <Card
      className={`relative w-full max-w-md mx-auto overflow-hidden rounded-[28px] border border-slate-200/80 bg-white text-slate-900 shadow-xl shadow-slate-900/10 transition-[transform,opacity] duration-700 ease-out hover:-translate-y-1.5 dark:border-slate-800/70 dark:bg-slate-900/80 dark:text-slate-100 ${cardActivated ? 'opacity-100 translate-y-0 scale-100' : 'opacity-90 translate-y-6 scale-[0.98]'}`}
    >
      <CardHeader className="space-y-3 border-b border-slate-100/80 px-6 pb-6 pt-6 dark:border-slate-800/70">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-accent dark:bg-accent/20 dark:text-accent">
              <span className="h-1.5 w-1.5 rounded-full bg-accent shadow-[0_0_0_6px_rgba(165,181,149,0.18)]" />
              Secure sign-in
            </span>
            <CardTitle className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Welcome back</CardTitle>
            <CardDescription className="text-slate-600 dark:text-slate-300">
              Access your leave dashboard to request time off and monitor approvals.
            </CardDescription>
          </div>
          <div className="hidden h-12 w-12 items-center justify-center rounded-2xl bg-accent/10 text-accent shadow-inner shadow-white/50 dark:bg-accent/20 dark:text-accent lg:flex">
            <Lock className="h-5 w-5" />
          </div>
        </div>
      </CardHeader>

      <CardContent className="relative space-y-6 px-6 pb-8 pt-6">
        <div
          // aria-hidden={formOpened ? 'false' : 'true'}
          className={`transition-[opacity,transform] duration-700 ease-out ${formReady ? 'opacity-100 translate-y-0 scale-100' : 'pointer-events-none opacity-0 translate-y-8 scale-[0.98]'}`}
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div
                className={`transition-all duration-500 ${formReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                style={{ transitionDelay: formReady ? '80ms' : '0ms' }}
              >
                <FormField
                  name="email"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required htmlFor="email" className="text-sm font-medium text-slate-700 dark:text-slate-200">Email Address</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-4 h-4 dark:text-slate-300" />
                          <Input
                            type="email"
                            id="email"
                            placeholder="Enter your email"
                            autoComplete="email"
                            className="h-11 border-slate-300 bg-white pl-10 text-slate-900 placeholder:text-slate-500 dark:border-slate-600 dark:bg-slate-800/90 dark:text-slate-100 dark:placeholder:text-slate-400"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div
                className={`transition-all duration-500 ${formReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                style={{ transitionDelay: formReady ? '160ms' : '0ms' }}
              >
                <FormField
                  name="password"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required htmlFor="password" className="text-sm font-medium text-slate-700 dark:text-slate-200">Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-4 h-4 dark:text-slate-300" />
                          <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter your password"
                            autoComplete="current-password"
                            className="h-11 border-slate-300 bg-white pl-10 pr-12 text-slate-900 placeholder:text-slate-500 dark:border-slate-600 dark:bg-slate-800/90 dark:text-slate-100 dark:placeholder:text-slate-400"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-pressed={showPassword}
                            className="absolute right-0 top-0 h-full px-3 py-2 text-slate-500 hover:bg-transparent dark:text-slate-300 dark:hover:text-slate-100"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword
                              ? (
                                <EyeOff className="h-4 w-4" />
                              )
                              : (
                                <Eye className="h-4 w-4" />
                              )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div
                className={`transition-all duration-500 ${formReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                style={{ transitionDelay: formReady ? '220ms' : '0ms' }}
              >
                <div className="flex items-center justify-between pt-2">

                  <FormField
                    name="rememberMe"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            id="rememberMe"
                            name="rememberMe"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="border-2 border-accent focus:ring-2 dark:border-slate-400"
                          />
                          <Label htmlFor="rememberMe" className="text-sm font-normal text-slate-700 dark:text-slate-200">Remember me</Label>
                        </div>
                      </FormItem>
                    )}
                  />
                  <Button variant="link" className="h-auto px-0 text-sm text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-50" asChild>
                    <a href="/forgot-password">Forgot password?</a>
                  </Button>
                </div>
              </div>

              <div
                className={`transition-all duration-500 ${formReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                style={{ transitionDelay: formReady ? '280ms' : '0ms' }}
              >
                <Button
                  type="submit"
                  className="w-full h-11 rounded-lg bg-gradient-to-r from-accent to-[color-mix(in_oklab,var(--accent),white_18%)] text-accent-foreground font-semibold shadow-lg hover:from-[color-mix(in_oklab,var(--accent),white_8%)] hover:to-[color-mix(in_oklab,var(--accent),white_28%)] focus-visible:ring-2 focus-visible:ring-accent transition-all duration-300"
                  disabled={isLoading || loginMutation.isPending}
                >
                  {isLoading || loginMutation.isPending
                    ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Signing in...</span>
                      </div>
                    )
                    : (
                      <div className="flex items-center space-x-2">
                        <span>Sign in</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    )}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </CardContent>
    </Card>
  )
}
